import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { useToast } from '../../../../components/toastContext';
import { AddressPicker, type AddressValue } from '../../../../components/inputs/AddressPicker';
import { DateField } from '../../../../components/inputs/DateField';
import { FormFieldset } from '../../../../components/inputs/FormFieldset';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { uid } from '../../../../lib/uid';
import { focusValidationError } from '../../../../lib/validationFocus';
import { mySurveysApi, searchMyTrainingAddresses } from '../mySurveysApi';
import type { MySurveyEnrollment } from '../mySurveyTypes';

const ID = 'my-survey-enroll';

/** A section's title is BOLD — the format Learning & Development's own forms give theirs. Scoped to the legend. */
const SECTION_TITLE = '[&>legend]:font-semibold';

/** `YYYY-MM-DD` a number of days from today, on the calendar — local y/m/d at noon, never through an instant. */
function daysFromToday(days: number): string {
  const now = new Date();
  const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + days, 12);
  const pad = (n: number) => String(n).padStart(2, '0');

  return `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}`;
}

/**
 * Enroll — the step between accepting a training recommendation and starting its training
 * (product call, 13 Oct 2026): the employee names the TRAINING SCHOOL, required, and its LOCATION —
 * the standard address block (the `<AddressPicker>`, then the street line), optional, with NO
 * postal code — and the TRAINING DURATION, two calendar dates, both required. Start training, the
 * next step, records the dates the training actually runs, opening on the day it is clicked.
 *
 * Opened again over an enrollment, it corrects it — until the training is completed. The picker
 * searches this form's OWN feed: the catalog's general search needs `personnel.read`.
 */
export function MySurveyEnrollModal({
  surveyId,
  recommendationId,
  recommendationTitle,
  enrollment,
  onClose,
  onSaved,
}: {
  surveyId: string;
  recommendationId: string;
  /** The recommendation's title — the training IS what was recommended, so it is stated, never typed. */
  recommendationTitle: string;
  /** The enrollment already recorded — an edit — or null to enroll. */
  enrollment: MySurveyEnrollment | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const editing = enrollment !== null;

  const [school, setSchool] = useState(enrollment?.school ?? '');
  // `?? null` at the seam: off the wire an absent rung is undefined, and the picker holds nulls.
  const [location, setLocation] = useState<AddressValue>({
    province: enrollment?.province ?? null,
    cityMunicipality: enrollment?.cityMunicipality ?? null,
    barangay: enrollment?.barangay ?? null,
  });
  const [street, setStreet] = useState(enrollment?.streetAddress ?? '');
  // CALENDAR dates, strings end to end — `DateField` reads and writes `YYYY-MM-DD`.
  const [startDate, setStartDate] = useState(enrollment?.startDate ?? '');
  const [endDate, setEndDate] = useState(enrollment?.endDate ?? '');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const endBeforeStart = startDate !== '' && endDate !== '' && endDate < startDate;
  const canSave = school.trim() !== '' && startDate !== '' && endDate !== '' && !endBeforeStart && confirmed && !saving;

  /** Synthetic values, for exercising the form. A real place: the picker writes NAMES. */
  function generate() {
    setSchool(`Sample training school ${uid().slice(0, 6)}`);
    setLocation({ province: 'Rizal', cityMunicipality: 'Antipolo', barangay: null });
    setStreet(`${100 + Math.floor(Math.random() * 800)} Sample Street`);
    // A training a week out, lasting five days.
    setStartDate(daysFromToday(7));
    setEndDate(daysFromToday(11));
  }

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await mySurveysApi.saveRecommendationEnrollment(surveyId, recommendationId, {
        school: school.trim(),
        province: location.province,
        cityMunicipality: location.cityMunicipality,
        barangay: location.barangay,
        streetAddress: street.trim() || null,
        startDate,
        endDate,
      });
      toast.success(editing ? 'Saved your enrollment.' : 'Enrolled in your training.');
      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Your enrollment could not be saved.', 0);

      setError(failure);
      // The three rungs are ONE control on the form.
      focusValidationError(failure, {
        idPrefix: ID,
        fieldIds: {
          province: `${ID}-location`,
          cityMunicipality: `${ID}-location`,
          barangay: `${ID}-location`,
          streetAddress: `${ID}-street`,
        },
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit enrollment' : 'Enroll'}
      size="lg"
      id={`${ID}-modal`}
      autogenerate={{ onGenerate: generate, disabled: saving }}
      confirm={{ id: `${ID}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id={`${ID}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button id={`${ID}-submit`} variant="primary" onClick={() => void save()} disabled={!canSave} loading={saving} loadingLabel="Submitting">
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/* STATED, not asked: the training is the recommendation. A locked value is text, never a disabled input. */}
        <div className="flex flex-col gap-1">
          <p className="text-sm text-slate-700">Training</p>
          <p id={`${ID}-title`} className="text-sm text-slate-600">
            {recommendationTitle}
          </p>
        </div>

        <TextField
          id={`${ID}-school`}
          label="Training school"
          required
          maxLength={300}
          description="Where you are taking this training. Learning & Development sees it on the recommendation."
          value={school}
          onChange={(e) => setSchool(e.target.value)}
          error={school.trim() ? error?.fieldError('school') : 'Name the training school.'}
          disabled={saving}
        />

        {/* The standard address block, as the personnel form draws it — without its postal code. */}
        <FormFieldset legend="Location" className={SECTION_TITLE}>
          <AddressPicker
            id={`${ID}-location`}
            value={location}
            onChange={setLocation}
            search={searchMyTrainingAddresses}
            error={error?.fieldError('province') ?? error?.fieldError('cityMunicipality') ?? error?.fieldError('barangay')}
            disabled={saving}
          />

          <TextField
            id={`${ID}-street`}
            label="Lot No / Street / Bldg"
            placeholder="e.g. Lot 12, Block 3, Mango St., Greenview Subd."
            maxLength={300}
            value={street}
            onChange={(e) => setStreet(e.target.value)}
            error={error?.fieldError('streetAddress')}
            disabled={saving}
          />
        </FormFieldset>

        {/* The two dates are ONE answer — how long the training runs — so they share a legend. */}
        <FormFieldset legend="Training duration" className={SECTION_TITLE}>
          <div className="grid gap-4 sm:grid-cols-2">
            <DateField
              id={`${ID}-start-date`}
              label="Start date"
              required
              value={startDate}
              onChange={setStartDate}
              error={startDate ? error?.fieldError('startDate') : 'Choose the date the training starts.'}
              disabled={saving}
            />
            <DateField
              id={`${ID}-end-date`}
              label="End date"
              required
              value={endDate}
              onChange={setEndDate}
              min={startDate || undefined}
              error={
                !endDate
                  ? 'Choose the date the training ends.'
                  : endBeforeStart
                    ? 'The training cannot end before it starts.'
                    : error?.fieldError('endDate')
              }
              disabled={saving}
            />
          </div>
        </FormFieldset>
      </div>
    </Modal>
  );
}
